import { useCallback, useEffect, useRef } from 'react'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import { chainContaining, chainsContainingAny } from '@/lib/text-linking/text-chain-mutator'
import { chainsAffectedByWrapSourceChange } from '@/lib/text-linking/chain-wrap-reflow-targets'
import { projectShapeToLinkable } from '@/lib/text-linking/linkable-text-frame'
import type { TextBoxData } from '@/lib/text/types/text-box-data'

import { groupGeometricPersistOpFromTransformSource } from '@/api/services/group-actions.service'
import type { StowedElement } from '@/api/types/stowed-element'
import type { UngroupElementsRequest } from '@/api/types/group-elements-api'
import {
  buildStowedVisibleRectOptions,
  isStowedTextElement,
} from '@/lib/elements/stowed-element-builder'
import { buildGroupTransformPersistBatch } from '@/lib/groups/build-group-transform-persist-batch'
import {
  buildRestructureBodyAsGroupSurvivor,
  buildRestructureBodyWrappedMultiGroups,
  buildUngroupElementsApiBody,
} from '@/lib/groups/build-group-api-payloads'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { logGroupRotateDebug } from '@/lib/debug/shape-geometry-debug'
import { logger } from '@/utils/logger'
import type { ShapeHistoryLifecycleMeta } from '@/lib/history/types/shape-history-lifecycle-meta'
import { resolvePlacedPhotoIdentity } from '@/lib/photos/fill-shape-with-photo'
import {
  reconcilePhotoPlacementOverridesWithShapes,
  syncPhotoPlacementOverrideAfterShapeRemoved,
} from '@/lib/photos/sync-photo-placement-from-shapes'
import {
  reconcileClikArtPlacementsWithShapes,
  resolvePlacedClikArtId,
  syncClikArtPlacementsAfterShapeRemoved,
} from '@/lib/clikart/sync-clikart-placement-from-shapes'
import { syncTemplatePlacementsWithShapes } from '@/lib/templates/sync-template-placement-from-shapes'
import { cancelPendingGroupPersists } from '@/lib/groups/pending-group-persist'
import { isShapeOutsidePasteboardBounds } from '@/lib/groups/pasteboard-bounds'

import { buildStowedShapeElement } from '../../helpers/build-stowed-shape-element'
import { buildGroupRepositionPayload } from '../../helpers/build-group-reposition-payload'
import { diffShapeUndoMeta } from '../../helpers/diff-shape-undo-meta'
import { resolveChainReflowAnchors } from '../../helpers/resolve-chain-reflow-anchors'
import { resolveBatchItemPersistOperation } from '../../helpers/resolve-batch-item-persist-operation'
import { revertCommitOutsidePasteboard } from '../../handlers/revert-commit-outside-pasteboard'
import { useWrapAffectedTextFrameResave } from '../use-wrap-affected-text-frame-resave'
import { chainFrameFit } from '../../text-chain/chain-frame-fit'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomeHistory,
  HomePersistence,
  HomeShapeEditing,
  HomeTextChains,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/**
 * Persisting canvas edits: the single-shape commit (`handleShapeCommitted`), batch commits with
 * mixed-transform coalescing, layer reorder, and shape deletion — each enqueueing the server
 * write, pushing history and keeping linked text chains intact.
 */
export function useHomeShapePersistence(
  deps: Pick<
    HomeFoundation,
    | 'textChains'
    | 'shapeManagerRef'
    | 'shapeTextBakeRef'
    | 'centerCanvasRef'
    | 'fontLookupRef'
    | 'deleteGestureScopeRef'
  > &
    Pick<HomeZoomAndSpread, 'spreadIdRef'> &
    Pick<HomeDocumentLayout, 'resolvedLayout' | 'spreadPlacementBounds' | 'spreadProjection'> &
    Pick<HomeToolsAndSelection, 'setActiveObject' | 'activeObjectId' | 'setActiveObjectId'> &
    Pick<
      HomePersistence,
      | 'pushAction'
      | 'enqueueUpdate'
      | 'enqueueDelete'
      | 'enqueueUngroup'
      | 'enqueueRestructure'
      | 'enqueueReposition'
      | 'markEdited'
      | 'pendingUngroupChildrenByTxnRef'
      | 'isGroupShape'
      | 'isGroupTxn'
      | 'isUngroupTxn'
      | 'isParentGroupOnlyUndo'
      | 'syncActiveFromShapeSelection'
    > &
    Pick<HomeHistory, 'flushPendingShapeDeleteHistory' | 'queueShapeDeleteHistory'> &
    Pick<HomeTextChains, 'redistributeChainStory' | 'repairChainAfterFrameDeleted'> &
    Pick<HomeShapeEditing, 'handleBatchShapesCommittedRef'>
) {
  const {
    textChains,
    shapeManagerRef,
    shapeTextBakeRef,
    centerCanvasRef,
    fontLookupRef,
    deleteGestureScopeRef,
    spreadIdRef,
    resolvedLayout,
    spreadPlacementBounds,
    spreadProjection,
    setActiveObject,
    activeObjectId,
    setActiveObjectId,
    pushAction,
    enqueueUpdate,
    enqueueDelete,
    enqueueUngroup,
    enqueueRestructure,
    enqueueReposition,
    markEdited,
    pendingUngroupChildrenByTxnRef,
    isGroupShape,
    isGroupTxn,
    isUngroupTxn,
    isParentGroupOnlyUndo,
    syncActiveFromShapeSelection,
    flushPendingShapeDeleteHistory,
    queueShapeDeleteHistory,
    redistributeChainStory,
    repairChainAfterFrameDeleted,
    handleBatchShapesCommittedRef,
  } = deps

  const resaveWrapAffectedTextFrames = useWrapAffectedTextFrameResave(deps)

  const handleShapeCommitted = useCallback(
    (
      shape: ShapeData,
      source?: 'move' | 'resize' | 'rotate' | 'update',
      undo?: ShapeCommittedUndoMeta
    ) => {
      if (activeObjectId === shape.id) {
        setActiveObject(shape)
      }

      // Pointer interactions can end with no geometric delta (e.g. click without drag).
      // ShapeManager omits `undo` in those no-op cases, so skip persistence/history noise.
      if (!undo && (source === 'move' || source === 'resize' || source === 'rotate')) {
        return
      }

      // Pasteboard-boundary guard: revert a commit that pushes the shape (any part of its
      // rotation-aware bounding box) outside the pasteboard's fixed work area, before any
      // persistence/history side effects below run. The pasteboard is a fixed document-space
      // extent — a shape left outside it would otherwise silently grow the canvas's native
      // scrollable area past that intended limit (see isShapeOutsidePasteboardBounds).
      if (
        undo &&
        isShapeOutsidePasteboardBounds(shape, resolvedLayout.bleedWidth, resolvedLayout.bleedHeight)
      ) {
        revertCommitOutsidePasteboard(shapeManagerRef.current, [
          { shapeId: shape.id, before: undo.before },
        ])

        return
      }

      // Panel-originated commits (e.g. Object Panel rotate button) call onShapeCommitted
      // directly, bypassing center-canvas's handleShapeCommitted wrapper. Sync the node-edit
      // overlay here so it follows the shape change regardless of origin.
      centerCanvasRef.current?.syncNodeEditShapeFromManager()

      const parentGroupChanged = undo && undo.before.parentGroupId !== undo.after.parentGroupId
      const structureOnlyParentChange = isParentGroupOnlyUndo(undo)

      if (parentGroupChanged && undo?.historyTxnId && isUngroupTxn(undo.historyTxnId)) {
        const beforeParent = undo.before.parentGroupId

        if (beforeParent) {
          let byGroup = pendingUngroupChildrenByTxnRef.current.get(undo.historyTxnId)

          if (!byGroup) {
            byGroup = new Map()
            pendingUngroupChildrenByTxnRef.current.set(undo.historyTxnId, byGroup)
          }

          let children = byGroup.get(beforeParent)

          if (!children) {
            children = new Set()
            byGroup.set(beforeParent, children)
          }

          children.add(shape.id)
        }
      }

      if (
        parentGroupChanged &&
        undo?.historyTxnId &&
        !isGroupTxn(undo.historyTxnId) &&
        !isUngroupTxn(undo.historyTxnId)
      ) {
        enqueueRestructure({
          moves: [
            {
              nodeId: shape.id,
              fromParentGroupId: undo.before.parentGroupId,
              toParentGroupId: undo.after.parentGroupId,
            },
          ],
          meta: {
            historyTxnId: undo.historyTxnId,
          },
        })
      }

      // Group resize can scale descendant text box width/height (move and pure rotation are
      // isometries that never change them — see shape-group-descendant-transform.ts), creating
      // new overflow for any chain-linked text inside the group. This must share one
      // historyTxnId with the group's own undo entries below so a single Ctrl+Z
      // reverts the whole gesture, including any reflow cascade — mirroring the fix applied to
      // handleTextBoxCommitted for the single-box case.
      //
      // The rest of this comment used to claim pointer-drag group resize was "separately handled via
      // handleBatchTextBoxesCommitted ... which already reflows correctly — do not duplicate that
      // here". That handler no longer exists, and its successor `handleBatchShapesCommitted` had no
      // chain reflow at all, so between the two of them nothing reflowed a batched resize. Both ends
      // are wired now: the rider lookup below, and an equivalent pass at the end of
      // `handleBatchShapesCommitted`. They cannot double-reflow — a gesture arrives at one or the
      // other, never both — and `redistributeChainStory` is idempotent on an already-settled chain
      // in any case.
      let effectiveHistoryTxnId = undo?.historyTxnId

      // Live chain reflow for a shape converted to a text frame (WMPCM-7988): content and resize
      // commits can create new overflow that must cascade into the rest of the chain. Move and
      // rotate are excluded for the same reason as Text-tool frames — `getShapeTextFlow` slices the
      // shape's path in its own local space, so neither changes how much text fits. Decided up
      // front, and a txn id synthesized when the commit has none, so the reflow's undo actions
      // share this edit's transaction and one Ctrl+Z reverts both (same reasoning as the group
      // transform case below and `handleTextBoxCommitted`).
      const shapeChainUid = shape.uid != null ? String(shape.uid) : null
      const willReflowChain =
        (source === 'update' || source === 'resize') &&
        shapeChainUid != null &&
        chainContaining(textChains, shapeChainUid) != null

      // A wrap-source shape (an obstacle with Text Wrap applied) is usually NOT itself a chain
      // member, so `willReflowChain` above never catches it — moving it onto/off of a linked
      // frame, resizing it, or toggling its wrap style all change that frame's capacity but
      // nothing redistributed the chain, so displaced text was silently dropped instead of
      // flowing into the next frame (WMPCM linked-text-wrap reflow gap).
      const wrapSourceReflowTargets = undo
        ? chainsAffectedByWrapSourceChange(
            { ...shape, ...undo.before } as ShapeData,
            shape,
            textChains,
            shapeManagerRef.current?.getShapeTextWrapContext()?.allShapes ?? [],
            shapeManagerRef.current?.getShapeTextWrapContext()?.zIndexOf ?? (() => undefined)
          )
        : []

      if ((willReflowChain || wrapSourceReflowTargets.length > 0) && !effectiveHistoryTxnId) {
        effectiveHistoryTxnId = `textlink-reflow:${crypto.randomUUID()}`
      }

      if (!isGroupShape(shape) && !structureOnlyParentChange) {
        void markEdited([shape.id])
        enqueueUpdate(
          buildStowedShapeElement(
            shapeTextBakeRef.current,
            shape,
            buildStowedVisibleRectOptions(
              shape.rotation,
              source === 'move'
                ? 'move'
                : source === 'rotate'
                  ? 'rotate'
                  : source === 'resize'
                    ? 'resize'
                    : 'update',
              source
            ),
            spreadProjection,
            fontLookupRef.current ?? undefined
          ),
          {
            operation: source === 'move' ? 'move' : source === 'rotate' ? 'rotate' : 'update',
            meta: undo?.historyTxnId ? { historyTxnId: undo.historyTxnId } : undefined,
          }
        )
      } else if (
        isGroupShape(shape) &&
        (source === 'move' || source === 'resize' || source === 'rotate')
      ) {
        const shapes = shapeManagerRef.current?.getShapes() ?? []
        const textBoxes: TextBoxData[] = []
        const geometricOperation = groupGeometricPersistOpFromTransformSource(source)
        const stowedBatch = buildGroupTransformPersistBatch(
          shapes,
          shape,
          textBoxes,
          geometricOperation,
          spreadProjection,
          fontLookupRef.current ?? undefined
        )

        void markEdited(stowedBatch.map((row) => row.objectId))

        if (source === 'rotate') {
          logGroupRotateDebug({
            label: 'committed',
            group: shape,
            descendants: shapes.filter((s) => s.parentGroupId === shape.id),
            stowedBatch,
          })
        }

        // Identify text rows by wire content, not kindId — a text frame serializes as a plain
        // kindId-1 rectangle (content.text), same as any other shape carrying paragraphs.
        const resizedTextUids =
          source === 'resize'
            ? stowedBatch
                .filter((row) => isStowedTextElement(row))
                .map((row) => shapes.find((s) => s.id === row.objectId)?.uid)
                .filter((uid): uid is number => uid != null)
                .map(String)
            : []
        const affectedChains =
          resizedTextUids.length > 0 ? chainsContainingAny(textChains, resizedTextUids) : []

        if (affectedChains.length > 0 && !effectiveHistoryTxnId) {
          effectiveHistoryTxnId = `group-transform:${crypto.randomUUID()}`
        }

        enqueueReposition(
          buildGroupRepositionPayload(shape, geometricOperation, stowedBatch, {
            historyTxnId: effectiveHistoryTxnId,
          })
        )

        // Riders resolve from the live SHAPE list. This read `textBoxes` until now — the literal `[]`
        // above, left behind when TextManager was deleted — so the rider was permanently
        // `undefined` and this reflow never ran once: resizing a group containing a linked text
        // frame left the chain un-reflowed, while every line that feeds it (`resizedTextUids`,
        // `affectedChains`, the synthesized `group-transform:` txn id) was computed and thrown
        // away. Same permanently-falsy-guard class as Known Gap #35 in CLAUDE.md.
        for (const { anchorUid, riderId } of resolveChainReflowAnchors(
          affectedChains,
          resizedTextUids,
          shapes
        )) {
          redistributeChainStory(textChains, anchorUid, riderId, effectiveHistoryTxnId!)
        }
      } else if (isGroupShape(shape) && source === 'update' && !structureOnlyParentChange) {
        // Non-geometric group update (lock/freeze flag changes). The reposition branch above
        // handles move/resize/rotate via GroupActionQueue; this branch persists property-only
        // changes on the group row itself via ChangeQueue.
        //
        // Excluding structureOnlyParentChange matters: groupSelectedObjects (shape-manager/hooks/use-shape-grouping.ts)
        // calls onShapeCommitted(child, 'update', {before/after: parentGroupId only}) for EVERY
        // selected root being reparented into the new wrapper group — including a pre-existing
        // group being wrapped. Without this guard, that fired a ChangeQueue enqueueUpdate of the
        // wrapped group's *stale* pre-wrap geometry (via buildStowedElement, which never sets
        // `elements` at all) on a 300ms debounce — racing the wrap's own flattened restructure
        // POST (persistForwardGroupApiFromShapes, sent via GroupActionQueue's near-immediate
        // microtask flush) and, since PUT /elements/update is a full replacement, arriving later
        // and clobbering the correct (flattened, all-members) geometry/children with the stale,
        // pre-wrap ones — the exact "3rd shape / text box missing after reload" symptom. A pure
        // parentGroupId change on a group is already fully handled by the group-creation/
        // restructure flow; it must not also go through the generic per-shape update path.
        //
        // A group that directly wraps a pre-existing nested group never gets a row of its own on
        // the backend: persistForwardGroupApiFromShapes flattens it onto a "survivor" (the earliest
        // pre-existing nested group) via POST /restructure (see build-group-api-payloads.ts). A
        // plain enqueueUpdate here would target `shape.id` — an objectId the backend has never
        // heard of — so a freeze/lock toggle on that wrapper would silently vanish on reload. Sync
        // the flag onto the survivor (the row that actually persists) and go through the same
        // restructure path instead.
        const shapesForWrapCheck = shapeManagerRef.current?.getShapes() ?? []
        const textBoxesForWrapCheck: TextBoxData[] = []
        let wrappedSurvivorBody: StowedElement[] | null = null

        try {
          wrappedSurvivorBody = buildRestructureBodyWrappedMultiGroups(
            shapesForWrapCheck,
            shape.id,
            textBoxesForWrapCheck,
            spreadProjection
          )
        } catch (e) {
          logger.warn('home: failed to build wrap-multi-group restructure body for group update', e)
        }

        const survivorId = wrappedSurvivorBody?.[0]?.objectId

        if (survivorId && survivorId !== shape.id) {
          shapeManagerRef.current?.updateShape(
            survivorId,
            { frozen: shape.frozen === true, locked: shape.locked === true },
            { bypassEditGuards: true }
          )

          try {
            wrappedSurvivorBody = buildRestructureBodyWrappedMultiGroups(
              shapeManagerRef.current?.getShapes() ?? [],
              shape.id,
              [],
              spreadProjection
            )
          } catch (e) {
            logger.warn(
              'home: failed to rebuild wrap-multi-group restructure body after survivor sync',
              e
            )
          }
        }

        if (wrappedSurvivorBody && wrappedSurvivorBody.length > 0) {
          const byId = new Map(shapesForWrapCheck.map((s) => [s.id, s]))
          const edited = new Set<string>([shape.id])

          for (const s of shapesForWrapCheck) {
            if (isDescendantOfGroup(s, shape.id, byId)) edited.add(s.id)
          }

          void markEdited([...edited])
          enqueueRestructure({
            moves: [],
            meta: undo?.historyTxnId ? { historyTxnId: undo.historyTxnId } : undefined,
            apiBody: wrappedSurvivorBody,
          })
        } else {
          void markEdited([shape.id])
          enqueueUpdate(
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              shape,
              buildStowedVisibleRectOptions(shape.rotation, 'update'),
              spreadProjection,
              fontLookupRef.current ?? undefined
            ),
            {
              operation: 'update',
              meta: undo?.historyTxnId ? { historyTxnId: undo.historyTxnId } : undefined,
            }
          )
        }
      }

      if (undo) {
        const { before, after, hasHistory, freezeStateChanged } = diffShapeUndoMeta(undo)

        if (hasHistory) {
          pushAction({
            type: 'update',
            shapeId: shape.id,
            before,
            after,
            historyTxnId: effectiveHistoryTxnId,
          })
        }

        if (freezeStateChanged) {
          syncActiveFromShapeSelection()
        }
      }

      if (willReflowChain && shapeChainUid) {
        // No edit-shape options any more (D4). Whether the text got shorter, whether the seam it
        // crossed was mid-paragraph, and whether a backward pull is therefore allowed were three
        // reconstructions of facts the story simply has: the chain is re-split from the head, so a
        // shortening edit reclaims and a lengthening one cascades, by the same code path.
        redistributeChainStory(textChains, shapeChainUid, shape.id, effectiveHistoryTxnId!)
      }

      // A wrap-source obstacle's own commit re-splits every chain it affects — see
      // `wrapSourceReflowTargets` above. Excludes any chain `willReflowChain` already redistributed
      // above (the shape can't be both the wrap source and a member of its own affected chain, since
      // `chainsAffectedByWrapSourceChange` skips the committed shape itself) so this never
      // double-redistributes the same chain in one commit.
      for (const target of wrapSourceReflowTargets) {
        redistributeChainStory(textChains, target.anchorUid, target.riderId, effectiveHistoryTxnId!)
      }

      if (undo && !structureOnlyParentChange) {
        resaveWrapAffectedTextFrames(
          [{ before: { ...shape, ...undo.before } as ShapeData, after: shape }],
          effectiveHistoryTxnId,
          new Set([shape.id])
        )
      }

      // Re-check placed-on-spread status after any move/resize/rotate of a placed photo — not
      // just legacy kindId-4 shapes. Every photo placed by current code is kindId 1/2/9/10 with
      // a `photoFill` (see resolvePlacedPhotoIdentity); the old kindId-4-only gate left the
      // "Placed" tray badge stuck true after dragging a modern placed photo off the spread onto
      // the pasteboard, since nothing else re-reconciled it (WMPCM Placed-badge follow-up).
      if (resolvePlacedPhotoIdentity(shape)) {
        reconcilePhotoPlacementOverridesWithShapes(
          shapeManagerRef.current?.getShapes() ?? [],
          spreadPlacementBounds,
          spreadIdRef.current
        )
      }

      // Same for a placed ClikArt: moving it on or off the spread updates its gallery badge.
      if (resolvePlacedClikArtId(shape)) {
        reconcileClikArtPlacementsWithShapes(
          shapeManagerRef.current?.getShapes() ?? [],
          spreadPlacementBounds,
          spreadIdRef.current
        )
      }
    },
    [
      spreadPlacementBounds,
      activeObjectId,
      enqueueReposition,
      enqueueRestructure,
      enqueueUpdate,
      isGroupShape,
      isGroupTxn,
      isParentGroupOnlyUndo,
      isUngroupTxn,
      markEdited,
      pushAction,
      redistributeChainStory,
      resaveWrapAffectedTextFrames,
      resolvedLayout,
      setActiveObject,
      shapeManagerRef,
      spreadProjection,
      syncActiveFromShapeSelection,
      textChains,
      centerCanvasRef,
      fontLookupRef,
      shapeTextBakeRef,
      spreadIdRef,
      pendingUngroupChildrenByTxnRef,
    ]
  )

  const pendingMixedTransformTxnRef = useRef<{
    historyTxnId: string
    source: 'move' | 'resize' | 'rotate'
    createdAtMs: number
  } | null>(null)

  /** Text ids already included on `enqueueReposition` with a group row; skip duplicate `enqueueUpdate`. */
  const textIdsCoalescedIntoLastGroupShapeBatchRef = useRef<Set<string>>(new Set())

  const handleBatchShapesCommitted = useCallback(
    (
      items: BatchShapeTransformItem[],
      source: 'rotate' | 'move' | 'align' | 'resize' | 'update',
      meta?: { historyTxnId?: string }
    ) => {
      if (items.length === 0) return

      const mgr = shapeManagerRef.current

      if (!mgr) return

      // Pasteboard-boundary guard: if committing this batch would leave ANY item outside the
      // pasteboard's fixed work area, revert the WHOLE batch (not just the offending shape) so a
      // multi-select drag's relative offsets stay intact, and show one combined notice.
      const shapesForBoundsCheck = mgr.getShapes()
      const shapesByIdForBoundsCheck = new Map(shapesForBoundsCheck.map((s) => [s.id, s]))
      const violatesPasteboardBounds = items.some((item) => {
        const shape = item.shape ?? shapesByIdForBoundsCheck.get(item.shapeId)

        return (
          shape != null &&
          isShapeOutsidePasteboardBounds(
            shape,
            resolvedLayout.bleedWidth,
            resolvedLayout.bleedHeight
          )
        )
      })

      if (violatesPasteboardBounds) {
        revertCommitOutsidePasteboard(mgr, items)

        return
      }

      textIdsCoalescedIntoLastGroupShapeBatchRef.current.clear()

      // A wrap-source shape (an obstacle with Text Wrap applied) reaches the manager through THIS
      // handler even for a single selected shape — the Effects panel's "apply Text Wrap" control
      // always calls `onBatchShapesCommitted(shapeItems, 'update')`, never `onShapeCommitted`, so
      // `handleShapeCommitted`'s own `wrapSourceReflowTargets` never sees it. Computed before
      // `historyTxnId` below, not after: a single-item `source: 'update'` batch (exactly this
      // panel's call) previously synthesized no id at all, so there was nothing for the reflow this
      // enables to share an undo step with.
      const wrapSourceReflowTargets =
        source === 'update'
          ? items.flatMap((item) => {
              const afterShape =
                item.shape ?? shapesForBoundsCheck.find((s) => s.id === item.shapeId)

              if (!afterShape) return []

              const beforeShape = { ...afterShape, ...item.before } as ShapeData

              return chainsAffectedByWrapSourceChange(
                beforeShape,
                afterShape,
                textChains,
                mgr.getShapeTextWrapContext()?.allShapes ?? [],
                mgr.getShapeTextWrapContext()?.zIndexOf ?? (() => undefined)
              )
            })
          : []

      const isMixedTransformSource = source === 'move' || source === 'resize' || source === 'rotate'
      const historyTxnId = isMixedTransformSource
        ? (meta?.historyTxnId ??
          `mixed-${source}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`)
        : (meta?.historyTxnId ??
          (source === 'update' && (items.length > 1 || wrapSourceReflowTargets.length > 0)
            ? `property-batch:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`
            : undefined))

      if (isMixedTransformSource && historyTxnId) {
        pendingMixedTransformTxnRef.current = {
          historyTxnId,
          source,
          createdAtMs: Date.now(),
        }
      } else {
        pendingMixedTransformTxnRef.current = null
      }

      const mixedChromeBefore =
        source === 'rotate' || source === 'resize'
          ? (centerCanvasRef.current?.consumePendingUnifiedRotateChromeBefore() ?? undefined)
          : undefined
      const mixedChromeAfter =
        source === 'rotate' || source === 'resize'
          ? (centerCanvasRef.current?.getUnifiedRotateChromeSnapshot() ?? undefined)
          : undefined

      // Only snapshot oriented multi-select / rotate chrome for rotate and resize. For plain moves,
      // `consumePendingRotateChromeBefore()` can still return a stale rotate snapshot (left over from
      // an earlier gesture) even though this batch is only translation — storing it makes undo call
      // `restoreUnifiedMultiSelectChrome` and draw a dashed box behind a single selected group.
      const captureOrientedSelectionChrome = source === 'rotate' || source === 'resize'

      pushAction({
        type: 'batchUpdate',
        items: items.map((i) => ({
          shapeId: i.shapeId,
          before: i.before,
          after: i.after,
        })),
        historyTxnId,
        multiSelectChromeBefore: captureOrientedSelectionChrome
          ? (mixedChromeBefore ?? mgr.consumePendingRotateChromeBefore() ?? undefined)
          : undefined,
        multiSelectChromeAfter: captureOrientedSelectionChrome
          ? (mixedChromeAfter ?? mgr.getMultiSelectChromeSnapshot() ?? undefined)
          : undefined,
      })

      if (!captureOrientedSelectionChrome) {
        // Discard any pending unified rotate snapshot so it cannot leak into a later history row
        // (e.g. after several move/undo cycles when `skipNextUnifiedRotateChromeResetRef` deferred clearing).
        centerCanvasRef.current?.consumePendingUnifiedRotateChromeBefore()
      }

      const shapes = mgr.getShapes()
      const byId = new Map(shapes.map((s) => [s.id, s]))
      const batchGroupIds = new Set<string>()

      for (const i of items) {
        const s = i.shape ?? shapes.find((c) => c.id === i.shapeId)

        if (s && isGroupShape(s)) batchGroupIds.add(s.id)
      }

      for (const i of items) {
        const shape = i.shape ?? shapes.find((c) => c.id === i.shapeId)

        if (shape) {
          if (isGroupShape(shape)) {
            const geometricOperation = groupGeometricPersistOpFromTransformSource(source)

            const textBoxes: TextBoxData[] = []
            const stowedBatch = buildGroupTransformPersistBatch(
              shapes,
              shape,
              textBoxes,
              geometricOperation,
              spreadProjection,
              fontLookupRef.current ?? undefined
            )

            for (const row of stowedBatch) {
              if (isStowedTextElement(row)) {
                textIdsCoalescedIntoLastGroupShapeBatchRef.current.add(row.objectId)
              }
            }

            if (geometricOperation === 'rotate') {
              logGroupRotateDebug({
                label: 'batch',
                group: shape,
                descendants: shapes.filter((s) => s.parentGroupId === shape.id),
                stowedBatch,
              })
            }

            enqueueReposition(
              buildGroupRepositionPayload(
                shape,
                geometricOperation,
                stowedBatch,
                historyTxnId ? { historyTxnId } : undefined
              )
            )
            continue
          }

          // `enqueueReposition` already sends the kind-7 row, subtree shapes, and nested text in
          // `stowedBatch` (resize persists via PUT …/elements/update for full blobs) — avoid a duplicate ChangeQueue batch for the same gesture.
          const subtreePersistedWithGroupReposition =
            (source === 'rotate' || source === 'move' || source === 'resize') &&
            batchGroupIds.size > 0 &&
            [...batchGroupIds].some((gid) => isDescendantOfGroup(shape, gid, byId))

          if (subtreePersistedWithGroupReposition) {
            continue
          }

          const op = resolveBatchItemPersistOperation(i, source)

          enqueueUpdate(
            buildStowedShapeElement(
              shapeTextBakeRef.current,
              shape,
              buildStowedVisibleRectOptions(shape.rotation, op, source),
              spreadProjection,
              fontLookupRef.current ?? undefined
            ),
            {
              operation: op,
              meta: historyTxnId ? { historyTxnId } : undefined,
            }
          )
        }
      }

      const markEditedIds = new Set(items.map((i) => i.shapeId))

      for (const id of textIdsCoalescedIntoLastGroupShapeBatchRef.current) {
        markEditedIds.add(id)
      }

      void markEdited([...markEditedIds])

      // Multi-select resize has to re-cascade any chain it touched, exactly as a single frame's
      // resize does through `handleShapeCommitted`'s `willReflowChain`. This handler had no chain
      // reflow of any kind, so resizing two linked frames together (or a group holding one) left the
      // story split at the frames' OLD capacities until some later edit happened to redistribute it.
      //
      // `resize` only: move and rotate slice the shape's path in its own local space, so neither
      // changes how much text fits — the same exclusion `willReflowChain` makes and for the same
      // reason. The txn id is the batch's own, so the resize and the reflow it caused undo as one
      // step.
      if (source === 'resize' && historyTxnId) {
        // Group rows never reach the per-item branch below (they `continue` into
        // `enqueueReposition`), so a chain member nested inside a batched group is picked up from
        // the coalesced-text set that branch fills in rather than by re-walking the subtree here.
        const resizedShapeIds = new Set([
          ...items.map((i) => i.shapeId),
          ...textIdsCoalescedIntoLastGroupShapeBatchRef.current,
        ])
        const resizedUids = [...resizedShapeIds]
          .map((id) => byId.get(id)?.uid)
          .filter((uid): uid is number => uid != null)
          .map(String)

        for (const { anchorUid, riderId } of resolveChainReflowAnchors(
          resizedUids.length > 0 ? chainsContainingAny(textChains, resizedUids) : [],
          resizedUids,
          shapes
        )) {
          redistributeChainStory(textChains, anchorUid, riderId, historyTxnId)
        }
      }

      // A wrap-source shape's own batched commit (see `wrapSourceReflowTargets` above) re-splits
      // every chain it affects. Mutually exclusive with the resize cascade above — `source` is
      // never both `'resize'` and `'update'` for one commit — so this never double-redistributes
      // the same chain; still deduped by anchor since one batch can name the same chain twice
      // (two obstacles over the same frame, say).
      if (historyTxnId && wrapSourceReflowTargets.length > 0) {
        const reflowedAnchorUids = new Set<string>()

        for (const target of wrapSourceReflowTargets) {
          if (reflowedAnchorUids.has(target.anchorUid)) continue

          reflowedAnchorUids.add(target.anchorUid)
          redistributeChainStory(textChains, target.anchorUid, target.riderId, historyTxnId)
        }
      }

      // Unlinked text frames under any batched wrap source (the Effects panel's Text Wrap control
      // lands here) — see `enqueueWrapAffectedTextFrameSaves`. Batch members are already saved above.
      resaveWrapAffectedTextFrames(
        items.flatMap((item) => {
          const after = byId.get(item.shapeId) ?? item.shape

          return after ? [{ before: { ...after, ...item.before } as ShapeData, after }] : []
        }),
        historyTxnId,
        markEditedIds
      )

      syncActiveFromShapeSelection()

      // The unified multi-select bounding box (`unifiedMultiSelectBounds` in center-canvas/center-canvas.tsx)
      // is memoized against `unifiedBoundsVersion`, not recomputed on every render — a live
      // pointer-drag rotate/resize bumps it directly from inside CenterCanvas's own drag
      // handlers, but a batch commit driven from outside CenterCanvas (Layout panel's
      // Align/Space/Mirror) has no other way to trigger that recompute, leaving the selection
      // chrome rendered at its pre-commit bounds even though the shapes themselves moved.
      centerCanvasRef.current?.resyncUnifiedMultiSelectChromeFromSelection()

      reconcilePhotoPlacementOverridesWithShapes(
        mgr.getShapes(),
        spreadPlacementBounds,
        spreadIdRef.current
      )
      reconcileClikArtPlacementsWithShapes(
        mgr.getShapes(),
        spreadPlacementBounds,
        spreadIdRef.current
      )

      queueMicrotask(() => {
        textIdsCoalescedIntoLastGroupShapeBatchRef.current.clear()
      })
    },
    [
      spreadPlacementBounds,
      enqueueReposition,
      enqueueUpdate,
      isGroupShape,
      markEdited,
      pushAction,
      redistributeChainStory,
      resaveWrapAffectedTextFrames,
      resolvedLayout,
      shapeManagerRef,
      spreadProjection,
      syncActiveFromShapeSelection,
      textChains,
      centerCanvasRef,
      fontLookupRef,
      shapeTextBakeRef,
      spreadIdRef,
    ]
  )

  // Wire the bridge ref so useHomeShapeEditing's handleFillShapeWithColor (hook 10) can call
  // handleBatchShapesCommitted (defined here in hook 11) without reordering the groups.
  useEffect(() => {
    handleBatchShapesCommittedRef.current = handleBatchShapesCommitted
  }, [handleBatchShapesCommitted, handleBatchShapesCommittedRef])

  // These callbacks fire when a shape/text arrange completes. The manager has already updated
  // its internal array order by the time these run. FI persistence, undo history, and
  // markEdited are handled by handleUnifiedLayerArrange which fires immediately after in the
  // same synchronous event handler, with full cross-type context.
  const handleLayerReorder = useCallback((_beforeOrder: string[], _afterOrder: string[]) => {}, [])

  const handleShapeDeleted = useCallback(
    (shape: ShapeData, meta?: ShapeHistoryLifecycleMeta) => {
      const shapesBeforeRemove = shapeManagerRef.current?.getShapes() ?? []

      syncPhotoPlacementOverrideAfterShapeRemoved(
        shape,
        shapesBeforeRemove.filter((s) => s.id !== shape.id),
        spreadPlacementBounds,
        spreadIdRef.current
      )

      // Ungrouping reports the dissolved group here too, but its children live on — only a real
      // delete takes anything off the spread. `shapesBeforeRemove` still lists every shape the
      // gesture removes (the manager publishes on the next render), so the rest of the gesture's
      // ids are discounted too, or a later callback would re-mark a card an earlier one cleared.
      if (!(meta?.historyTxnId && isUngroupTxn(meta.historyTxnId))) {
        const gestureScope = deleteGestureScopeRef.current
        const inGestureScope = gestureScope != null && gestureScope.txnId === meta?.historyTxnId

        syncClikArtPlacementsAfterShapeRemoved(
          shape,
          shapesBeforeRemove,
          spreadPlacementBounds,
          spreadIdRef.current,
          inGestureScope ? gestureScope.ids : undefined
        )
        syncTemplatePlacementsWithShapes(
          shapesBeforeRemove,
          spreadIdRef.current,
          new Set([shape.id, ...(inGestureScope ? gestureScope.ids : [])])
        )
      }

      if (activeObjectId === shape.id) {
        setActiveObject(null)
        setActiveObjectId(null)
      }

      if (isGroupShape(shape)) {
        if (meta?.historyTxnId) {
          flushPendingShapeDeleteHistory()
          pushAction({
            type: 'remove',
            shape: { ...shape },
            insertionIndex: meta.historyInsertIndex,
            historyTxnId: meta.historyTxnId,
          })
        } else {
          queueShapeDeleteHistory(shape)
        }

        const txnId = meta?.historyTxnId

        if (txnId && isUngroupTxn(txnId)) {
          const grouped = pendingUngroupChildrenByTxnRef.current.get(txnId)
          const childIds = grouped?.get(shape.id)
          const orderedIds = childIds ? Array.from(childIds) : []
          const shapesAfter = shapeManagerRef.current?.getShapes() ?? []
          const nestedChildGroupIds = meta?.ungroupNestedChildGroupIds ?? []

          if (nestedChildGroupIds.length > 0) {
            const textBoxesAfter: TextBoxData[] = []

            for (const childGroupId of nestedChildGroupIds) {
              try {
                const apiBody = buildRestructureBodyAsGroupSurvivor(
                  shapesAfter,
                  childGroupId,
                  textBoxesAfter,
                  spreadProjection
                )

                if (apiBody && apiBody.length > 0) {
                  enqueueRestructure({
                    moves: [],
                    meta: { historyTxnId: txnId },
                    apiBody,
                  })
                }
              } catch (e) {
                logger.warn('home: failed to build restructure body for nested child group', e)
              }
            }

            const releasedNonGroupIds = meta?.ungroupReleasedNonGroupChildIds ?? []

            for (const id of releasedNonGroupIds) {
              const s = shapesAfter.find((x) => x.id === id)

              if (s) {
                void markEdited([id])
                enqueueUpdate(
                  buildStowedShapeElement(
                    shapeTextBakeRef.current,
                    s,
                    undefined,
                    spreadProjection,
                    fontLookupRef.current ?? undefined
                  ),
                  {
                    operation: 'update',
                    meta: { historyTxnId: txnId },
                  }
                )
              }
            }

            void markEdited([shape.id, ...orderedIds])
            enqueueDelete(shape.id, { meta: { historyTxnId: txnId } })
          } else {
            let apiBody: UngroupElementsRequest | undefined

            if (orderedIds.length > 0) {
              try {
                apiBody = buildUngroupElementsApiBody(orderedIds, shapesAfter, [], spreadProjection)
              } catch (e) {
                logger.warn('home: failed to build ungroup API body', e)

                if (grouped) {
                  grouped.delete(shape.id)
                  if (grouped.size === 0) {
                    pendingUngroupChildrenByTxnRef.current.delete(txnId)
                  }
                }

                return
              }
            }

            enqueueUngroup({
              groupId: shape.id,
              parentGroupId: shape.parentGroupId,
              childIds: orderedIds,
              insertIndex: meta?.historyInsertIndex,
              meta: {
                historyTxnId: txnId,
              },
              ...(apiBody && apiBody.length > 0 ? { apiBody } : {}),
            })
          }

          if (grouped) {
            grouped.delete(shape.id)
            if (grouped.size === 0) {
              pendingUngroupChildrenByTxnRef.current.delete(txnId)
            }
          }
        } else {
          // A group deleted while its creation is still waiting for its children to save (a placed
          // template): the request would make an empty group, so it is called off. Ungrouping is
          // left alone above, where the queued creation still goes out ahead of its ungroup.
          cancelPendingGroupPersists([shape.id])
          enqueueDelete(shape.id, txnId ? { meta: { historyTxnId: txnId } } : undefined)
        }

        return
      }

      if (meta?.historyTxnId) {
        flushPendingShapeDeleteHistory()
        pushAction({
          type: 'remove',
          shape: { ...shape },
          insertionIndex: meta.historyInsertIndex,
          historyTxnId: meta.historyTxnId,
        })
      } else {
        if (resolvePlacedPhotoIdentity(shape)) {
          const mgrForCaptions = shapeManagerRef.current
          const linkedCaptions =
            mgrForCaptions?.getShapes().filter((s) => s.captionForShapeId === shape.id) ?? []

          for (const caption of linkedCaptions) {
            const detached = { ...caption, captionForShapeId: undefined }

            mgrForCaptions!.updateShape(caption.id, { captionForShapeId: undefined })
            enqueueUpdate(
              buildStowedShapeElement(
                shapeTextBakeRef.current,
                detached,
                undefined,
                spreadProjection,
                fontLookupRef.current ?? undefined
              ),
              { operation: 'update' }
            )
          }
        }

        queueShapeDeleteHistory(shape)
      }

      // Chain repair (WMPCM-7988): a shape converted to a text frame can be a chain member, so
      // deleting it must splice the chain and reflow the remainder exactly as deleting a Text-tool
      // frame does — otherwise the chain dangles on a uid that no longer exists (TC-04).
      if (shape.uid != null) {
        repairChainAfterFrameDeleted(
          {
            id: shape.id,
            uid: shape.uid,
            paragraphs: shape.paragraphs ?? [],
            fit: chainFrameFit(
              projectShapeToLinkable(shape),
              fontLookupRef.current ?? undefined,
              shapeManagerRef.current?.getShapeTextWrapContext()
            ),
            shape,
          },
          meta?.historyTxnId
        )
      }

      enqueueDelete(
        shape.id,
        meta?.historyTxnId ? { meta: { historyTxnId: meta.historyTxnId } } : undefined
      )
    },
    [
      spreadPlacementBounds,
      activeObjectId,
      deleteGestureScopeRef,
      enqueueDelete,
      enqueueRestructure,
      enqueueUngroup,
      enqueueUpdate,
      flushPendingShapeDeleteHistory,
      isGroupShape,
      isUngroupTxn,
      repairChainAfterFrameDeleted,
      markEdited,
      pushAction,
      queueShapeDeleteHistory,
      setActiveObject,
      spreadProjection,
      fontLookupRef,
      shapeManagerRef,
      shapeTextBakeRef,
      spreadIdRef,
      pendingUngroupChildrenByTxnRef,
      setActiveObjectId,
    ]
  )

  return {
    handleShapeCommitted,
    pendingMixedTransformTxnRef,
    textIdsCoalescedIntoLastGroupShapeBatchRef,
    handleBatchShapesCommitted,
    handleLayerReorder,
    handleShapeDeleted,
  }
}
